import Vue from "vue";
import {
    getMentionsById, getPublicConversationFilter, getTreeMentions, getResharePreviews,
    getReshareTotal, CONVERSATION_STRUCTURE_SELECT, CONVERSATION_MENTION_LIMIT
} from "@/conversation/utils/GrouseConversationProxy";
import {createConversationTree} from "@/conversation/utils/ConversationTree";

export const CONVERSATION_TOO_LARGE = 'CONVERSATION_TOO_LARGE';

const PAGE_SIZE = 20;
const MISSING_PARENT_BATCH_SIZE = 100;
const MAX_MISSING_PARENT_ROUNDS = 100;
const internals = new WeakMap();
const cancelled = new Error("Conversation request cancelled");
const isPublic = mention => !mention.isDirectMessage &&
    (!mention.visibility || (mention.visibility.id ?? mention.visibility) === 'UNKNOWN');

/**
 * Loads a public conversation for display as an expandable reply thread.
 *
 * Conversations can contain tens of thousands of mentions. Fetching full
 * mentions for the entire conversation would transfer unnecessary data
 * and make the view expensive to render. Fetching replies individually would
 * instead require repeated requests to discover how the conversation connects.
 *
 * To avoid both problems, loading happens in two stages:
 * 1. Fetch a lightweight conversation structure to establish relationships and ordering.
 * 2. Fetch full mentions only for the portions being displayed.
 *
 * The structure also lets the view keep the selected mention connected to the
 * conversation head, using expandable placeholders for intervening mentions
 * rather than displaying every ancestor.
 *
 * The initial structure query requests one record beyond CONVERSATION_MENTION_LIMIT to
 * detect unsupported conversation sizes before constructing a partial tree.
 * This is not a cap on all records fetched: missing parents and reshares are
 * fetched separately.
 *
 * Missing parents may be irrelevant mentions needed to explain the reply
 * chain. Loading missing parents therefore allows irrelevant mentions while retaining
 * the public-only restriction. Unavailable parents remain as placeholders.
 *
 * Expansions are limited to 20 mentions at a time. Reshares are fetched
 * separately because they are shown as author previews, not reply branches.
 * Their displayed counts use public, relevant mentions rather than the
 * structure's reshareCount, which can include irrelevant mentions.
 *
 * The conversation structure is kept outside Vue's reactive state to avoid observing
 * a large relationship structure. Loading state and visible rows are reactive
 * so the view updates as mentions become available.
 *
 * Data loading is separate from layout and navigation so expanding, retrying
 * and cancelling requests do not depend on the rendered conversation.
 */
export class MentionConversationLoader {
    constructor(accountCode, selectedMention) {
        const conversationId = selectedMention.conversationId ?? selectedMention.id;
        this.state = Vue.observable({loading: false, error: null, conversationId,
            filter: getPublicConversationFilter(conversationId), rows: []});
        internals.set(this, {accountCode, selectedMention, generation: 0, disposed: false, requests: new Set(),
            operations: 0, actions: new Map(), tree: null, mentionsById: new Map(), exposed: new Set(),
            expansions: new Map(), selectedReshare: null, reshares: new Map()});
    }

    get loading() { return this.state.loading; }
    get error() { return this.state.error; }
    get filter() { return this.state.filter; }
    get conversationId() { return this.state.conversationId; }
    visibleRows() { return this.state.rows; }
    // Transitional data-only convenience for existing consumers.
    visibleMentions() { return this.visibleRows().filter(row => row.mention).map(row => row.mention); }

    _current(generation) {
        const data = internals.get(this);
        return !data.disposed && data.generation === generation;
    }

    async _request(request, generation) {
        const data = internals.get(this);
        if (!this._current(generation)) {
            request.cancel?.();
            request.catch(() => {});
            throw cancelled;
        }
        data.requests.add(request);
        try {
            const result = await request;
            if (!this._current(generation)) throw cancelled;
            return result;
        } finally {
            data.requests.delete(request);
        }
    }

    async _run(generation, work, action = false) {
        const data = internals.get(this);
        if (!this._current(generation)) return;
        if (!action) {
            data.operations++;
            this.state.loading = true;
            this.state.error = null;
        }
        try {
            await work();
        } catch (error) {
            if (this._current(generation) && error !== cancelled) {
                if (action) throw error;
                this.state.error = error;
            }
        } finally {
            if (this._current(generation) && !action) {
                data.operations--;
                this.state.loading = data.operations > 0;
            }
        }
    }

    _updateRows() {
        const data = internals.get(this);
        const {rows, expansions} = data.tree.project(data.exposed, data.mentionsById);
        data.expansions = expansions;
        const previous = new Map(this.state.rows.map(row => [row.id, row]));
        this.state.rows = rows.map(row => {
            const reshares = data.reshares.get(row.id);
            if (reshares) Object.assign(row, reshares);
            const selected = data.selectedReshare;
            if (selected?.reshareOfId === row.id) {
                row.reshares = [selected, ...row.reshares.filter(m => m.id !== selected.id)].slice(0, 2);
            }
            const old = previous.get(row.id);
            return old ? Object.assign(old, row) : row;
        });
    }

    async _loadMentions(ids, generation) {
        const data = internals.get(this);
        const missing = ids.filter(id => !data.mentionsById.has(id));
        if (missing.length) {
            const mentions = await this._request(getMentionsById(data.accountCode, missing), generation);
            for (const mention of mentions ?? []) {
                if (isPublic(mention) && !mention.reshareOfId) data.mentionsById.set(mention.id, mention);
            }
        }
        for (const id of ids) data.exposed.add(id);
    }

    /** Latest fetch wins. Calling again cancels in-flight work and starts a fresh conversation load. */
    async fetchData() {
        const data = internals.get(this);
        if (data.disposed) return this.visibleRows();
        for (const request of data.requests) request.cancel?.();
        const generation = ++data.generation;
        data.operations = 0;
        data.actions.clear();
        data.mentionsById = new Map();
        data.exposed = new Set();
        data.reshares = new Map();
        data.tree = null;
        data.selectedReshare = null;
        data.expansions = new Map();
        this.state.rows = [];
        await this._run(generation, async () => {
            if (!isPublic(data.selectedMention)) throw new Error("Only public conversations are available");
            // Re-fetch the selection with a public filter, not the unfiltered single-mention endpoint.
            const initialIds = [...new Set([data.selectedMention.id, this.conversationId])];
            const initial = await this._request(getMentionsById(data.accountCode, initialIds), generation);
            const selected = initial?.find(m => m.id === data.selectedMention.id && isPublic(m));
            if (!selected) throw new Error("The selected public mention is unavailable");
            this.state.conversationId = selected.conversationId ?? data.selectedMention.conversationId ?? selected.reshareOfId ?? selected.id;
            this.state.filter = getPublicConversationFilter(this.conversationId);
            for (const mention of initial ?? []) {
                if (isPublic(mention) && !mention.reshareOfId) data.mentionsById.set(mention.id, mention);
            }
            const focusId = selected.reshareOfId ?? selected.id;
            data.selectedReshare = selected.reshareOfId ? selected : null;
            const extraIds = [...new Set([this.conversationId, focusId])].filter(id => !data.mentionsById.has(id));
            if (extraIds.length) await this._loadMentions(extraIds, generation);

            const structure = await this._request(getTreeMentions(data.accountCode, {conversationId: this.conversationId}), generation);
            if (structure?.length > CONVERSATION_MENTION_LIMIT) {
                throw Object.assign(new Error('This conversation is too large to display'), {code: CONVERSATION_TOO_LARGE});
            }
            const nodes = new Map((structure ?? []).map(m => [m.id, m]));
            for (const mention of data.mentionsById.values()) nodes.set(mention.id, mention);
            // Ensure even an unavailable original has a row beside its public selected reshare.
            if (!nodes.has(focusId)) nodes.set(focusId, {id: focusId, replyToId: this.conversationId, unavailable: true});
            await this._loadMissingParents(nodes, generation);
            if (!this._current(generation)) return;
            data.tree = createConversationTree([...nodes.values()], this.conversationId);
            const ids = data.tree.initialIds(focusId, PAGE_SIZE);
            await this._loadMentions(ids, generation);
            data.exposed.add(focusId);
            data.exposed.add(this.conversationId);
            this._updateRows();
        });
        return this.visibleRows();
    }

    async _loadMissingParents(nodes, generation) {
        const data = internals.get(this);
        const attempted = new Set();
        // A corrupt API must not lead to an infinite stream of newly discovered ancestors.
        for (let round = 0; round < MAX_MISSING_PARENT_ROUNDS && attempted.size < CONVERSATION_MENTION_LIMIT; round++) {
            const missing = new Set();
            if (!nodes.has(this.conversationId)) missing.add(this.conversationId);
            for (const node of nodes.values()) {
                if (node.replyToId && !nodes.has(node.replyToId) && !attempted.has(node.replyToId)) missing.add(node.replyToId);
            }
            const ids = [...missing].filter(id => !attempted.has(id)).slice(0, CONVERSATION_MENTION_LIMIT - attempted.size);
            if (!ids.length) break;
            for (let i = 0; i < ids.length; i += MISSING_PARENT_BATCH_SIZE) {
                const batch = ids.slice(i, i + MISSING_PARENT_BATCH_SIZE);
                batch.forEach(id => attempted.add(id));
                const parents = await this._request(getMentionsById(data.accountCode, batch, CONVERSATION_STRUCTURE_SELECT), generation);
                for (const parent of parents ?? []) {
                    if (isPublic(parent) && batch.includes(parent.id)) nodes.set(parent.id, parent);
                }
            }
        }
    }

    /** Expand a more/gap row, or the hidden replies below a mention, by at most 20 full mentions. */
    async expand(row) {
        const data = internals.get(this);
        if (data.disposed || !data.tree) return this.visibleRows();
        const key = `expand:${row.id}`;
        if (data.actions.has(key)) return data.actions.get(key);
        const generation = data.generation;
        const work = this._run(generation, async () => {
            const target = data.expansions.has(row.id) ? row
                : this.visibleRows().find(candidate => candidate.kind === 'more' && candidate.parentId === row.id);
            const starts = data.expansions.get(target?.id) ?? [];
            const ids = data.tree.expansionIds(starts, data.exposed, PAGE_SIZE, target?.kind !== 'gap');
            if (!ids.length) return;
            await this._loadMentions(ids, generation);
            this._updateRows();
        }, true);
        data.actions.set(key, work);
        try { await work; } finally { if (this._current(generation)) data.actions.delete(key); }
        return this.visibleRows();
    }

    /** Two author previews and a separate relevant/public count, never the raw reshareCount. */
    async loadReshares(row) {
        const data = internals.get(this);
        const current = this.visibleRows().find(r => r.id === row.id);
        if (data.disposed || !current || !current.mention) return [];
        if (current.resharesLoaded) return current.reshares;
        // The structure count is only a hint that previews may exist, not a displayed count.
        const node = data.tree.getMention(row.id);
        if (!node?.reshareCount && data.selectedReshare?.reshareOfId !== row.id) return [];
        const key = `reshares:${row.id}`;
        if (data.actions.has(key)) {
            await data.actions.get(key);
            return current.reshares;
        }
        const generation = data.generation;
        const work = this._run(generation, async () => {
            current.loadingReshares = true;
            try {
                const [previews, total] = await Promise.all([
                    this._request(getResharePreviews(data.accountCode, row.id), generation),
                    this._request(getReshareTotal(data.accountCode, row.id), generation)
                ]);
                const state = {reshares: (previews ?? []).filter(isPublic).slice(0, 2),
                    reshareTotal: total?.mentionCount ?? 0, resharesLoaded: true, loadingReshares: false};
                data.reshares.set(row.id, state);
                this._updateRows();
            } finally {
                if (this._current(generation)) current.loadingReshares = false;
            }
        }, true);
        data.actions.set(key, work);
        try { await work; } finally { if (this._current(generation)) data.actions.delete(key); }
        return current.reshares;
    }

    /** Cancels requests (including queued Grouse requests) and suppresses all late state changes. */
    dispose() {
        const data = internals.get(this);
        data.disposed = true;
        data.generation++;
        for (const request of data.requests) request.cancel?.();
        data.requests.clear();
        data.actions.clear();
        this.state.loading = false;
        for (const row of this.state.rows) row.loadingReshares = false;
    }
}
